import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { MatDialog } from '@angular/material/dialog';
import { FileAssetsService } from '@services/assets/file-assets.service';
import { EnvironmentService } from '@services/environment/environment.service';
import { IbexCheckboxComponent } from '../../../../../components/ibex-checkbox/ibex-checkbox.component';
import { SelectAssetsComponent } from '../../../../../components/select-assets/select-assets.component';
import { DialogService } from '@services/dialog/dialog.service';
import { TranslateService } from '@services/translate/translate.service';
import { EditAssetComponent } from '../edit-asset/edit-asset.component';

@Component({
    selector: 'documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
    providers: [FileAssetsService],
    standalone: false
})
export class DocumentsComponent implements OnInit {

  @Input() dynamicContent;
  @Input() translationEntries;
  @Input() availableLanguages = [];
  @Input() packageLang;
  @Input() lang;
  @Input() parents = '';
  @Input() parentId = '';
  @ViewChild('checkboxAll') checkboxAll: IbexCheckboxComponent;
  @ViewChildren(IbexCheckboxComponent) checkboxes!: QueryList<IbexCheckboxComponent>;
  public imageAssetPath: any;
  public timestamp = Date.now();
  selectedFiles = [];
  checkBoxActions = [{ translation: 'dynamicContent.disconnectMultipleItems', icon: 'delete', action: () => { this.disconnectSelectedFiles() } }];

  public fileAssetPath: any;
  filesByPath = {};

  constructor(
    private dialog: MatDialog, private documentAssetsService: FileAssetsService, private environmentService: EnvironmentService,
    private dialogService: DialogService, private ts: TranslateService
  ) {
    this.fileAssetPath = this.environmentService.getFileAssetPath();
    this.imageAssetPath = this.environmentService.getImageAssetPath();
  }

  async ngOnInit() {
    const paths = [];
    if (this.dynamicContent) {
      for (const dataFile of this.dynamicContent.dataFiles) {
        paths.push(dataFile.url);
      }
    }
    if (this.lang !== this.dynamicContent.lang && this.translationEntries[this.lang]) {
      if (!this.translationEntries[this.lang].dataFiles) {
        this.translationEntries[this.lang].dataFiles = [];
      }
      for (const dataFile of this.translationEntries[this.lang].dataFiles) {
        paths.push(dataFile.url);
      }
    }
    if (paths.length) {
      const documents = await this.findDocumentsBatchWise(paths, 50);
      for (const doc of documents) {
        this.filesByPath[doc.path] = doc;
      }
    }
  }

  async findDocumentsBatchWise(paths: string[], batchSize = 100) {
    const requests: Promise<any>[] = [];
    for (let i = 0; i < paths.length; i += batchSize) {
      const batch = paths.slice(i, i + batchSize);
      requests.push(this.documentAssetsService.find(0, batchSize, { path: { $in: batch } }));
    }
    const results = await Promise.all(requests);
    return results.map(r => r.data).flat();
  }

  public async chooseFromPDFAssets(): Promise<any> {
    const assets: Models.ImageAsset[] = await this.dialog.open(SelectAssetsComponent, {
      width: '800px', height: '75%', panelClass: 'select-assets-modal',
      data: { type: 'file', title: 'Dokumente', multiple: true, parent: this.parents, parentId: this.parentId }
    }).afterClosed().toPromise();
    if (!assets?.length) {
      return;
    }
    assets.map(asset => {
      const file = new Models.FileObject();
      file.name = asset.originalFileName;
      file.mimeType = asset.mimeType;
      file.url = asset.path;
      this.filesByPath[asset.path] = asset;
      if (this.lang !== this.dynamicContent.lang) {
        if (!this.translationEntries[this.lang].dataFiles) {
          this.translationEntries[this.lang].dataFiles = [];
        }
        this.translationEntries[this.lang].dataFiles.push(file);
      } else {
        this.dynamicContent.dataFiles.push(file);
      }
    });
  }

  public async downloadFile(index){
    let assetPath = this.environmentService.getFileAssetPath();
    let asset = this.dynamicContent.dataFiles[index];
    const a = Object.assign(document.createElement('a'), { href: assetPath + asset.url + '?' + this.timestamp, download: asset.name, target: '_blank' });
    a.click();
    a.remove();
  }

  public async editFile(index) {
    const boundAsset = await this.documentAssetsService.get(this.filesByPath?.[this.dynamicContent?.dataFiles?.[index]?.url]?._id);
    await this.dialog.open(EditAssetComponent, {
      width: '875px', height: 'auto', panelClass: 'edit-media-modal', data: {
        asset: boundAsset, availableLanguages: this.availableLanguages, packageLang: this.packageLang,
        dataType: this.dynamicContent.type, isInContent: true, page: 'content', type: 'file'
      }
    }).afterClosed().toPromise();
    try {
      const res = await this.documentAssetsService.get(boundAsset._id);
      this.filesByPath[res.path] = res;
    } catch (err) {
      console.warn(err);
    }
  }

  public async disconnectFile(index) {
    const msg = this.ts.get('dynamicContent.deleteSelectedQuestion');
    this.dialogService.confirm(msg).then(res => {
      if (!res) {
        return;
      }
      this.dynamicContent.dataFiles.splice(index, 1);
    });
  }

  public disconnectSelectedFiles() {
    for (const file of this.selectedFiles) {
      if (this.lang === this.dynamicContent.lang) {
        this.dynamicContent.dataFiles.splice(this.dynamicContent.dataFiles.findIndex(el => el.url === file.url), 1);
      } else {
        this.translationEntries[this.lang].dataFiles.splice(this.translationEntries[this.lang].dataFiles.findIndex(el => el.url === file.url), 1);
      }
    }
    this.selectedFiles = [];
    this.checkboxes.forEach(checkbox => { checkbox.unselect(); });
  }

  public disconnectFileTranslation(index, lang) {
    this.translationEntries[lang].dataFiles.splice(index, 1);
  }

  public selectItem(file: any) {
    if (this.selectedFiles.map(el => el.url).includes(file.url)) {
      this.selectedFiles.splice(this.selectedFiles.map(el => el.url).indexOf(file.url), 1);
    } else {
      this.selectedFiles.push(file);
    }
    if (this.selectedFiles?.length === this.dynamicContent?.mediaFiles?.length) {
      this.checkboxAll.select();
    } else {
      this.checkboxAll.unselect();
    }
  }

  public selectItemsAll() {
    if (this.lang === this.dynamicContent.lang) {
      if (this.selectedFiles?.length === this.dynamicContent?.dataFiles?.length) {
        this.selectedFiles = [];
      } else {
        this.selectedFiles = JSON.parse(JSON.stringify(this.dynamicContent.dataFiles));
      }
    } else {
      if (this.selectedFiles?.length === this.translationEntries[this.lang]?.dataFiles?.length) {
        this.selectedFiles = [];
      } else {
        this.selectedFiles = JSON.parse(JSON.stringify(this.translationEntries[this.lang].dataFiles));
      }
    }
    this.checkboxes.filter(el => el.id !== 'checkboxAll').forEach(checkbox => {
      if (this.selectedFiles.length === 0) {
        checkbox.unselect();
      } else {
        checkbox.select();
      }
    });
  }

  public dropFile(event: any) {
    if (event.previousIndex !== event.currentIndex) {
      const item = this.dynamicContent.dataFiles.splice(event.previousIndex, 1)[0];
      this.dynamicContent.dataFiles.splice(event.currentIndex, 0, item);
    }
  }
}
